import { Ellipse, FabricText, Group, Line, Rect, Textbox } from 'fabric';
import type { FabricObject } from 'fabric';
import { applyFill } from '../fill';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type {
  ContentSourceSpec,
  FillValue,
  StandardContentPayload,
  ToolRecipe,
} from '../types';

/**
 * Frayer Model — the classic four-quadrant vocabulary/concept organizer: a
 * center oval TERM ringed by Definition · Characteristics · Examples ·
 * Non-Examples. Excellent fully manual (type a term and its four facets, get a
 * print-ready anchor) and Smart-fillable from a standard's concepts + examples.
 *
 * A `leaveBlanks` mode swaps the four content blocks for ruled writing lines —
 * the blank worksheet a teacher hands out — while keeping the term prompt.
 *
 * Stock primitives only (Rect / Ellipse / Line / Textbox / FabricText). Params
 * arrive untrusted; generate() coerces every read and never throws.
 */

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}
function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}
function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}
/** Multiline → trimmed, non-empty lines. */
function splitLines(v: unknown): string[] {
  return rawStr(v, '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}
function solidFromFill(fill: unknown, fallback: string): string {
  if (typeof fill === 'string') return fill.trim() || fallback;
  if (fill && typeof fill === 'object') {
    const f = fill as { type?: string; color?: string; from?: string; stops?: { color?: string }[] };
    if (f.type === 'solid' && typeof f.color === 'string') return f.color;
    if (f.type === 'linear-gradient' || f.type === 'radial-gradient') {
      if (Array.isArray(f.stops) && f.stops.length && f.stops[0].color) return f.stops[0].color;
      if (typeof f.from === 'string') return f.from;
    }
  }
  return fallback;
}
function readableInkOn(bg: string, ink: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(bg).trim());
  if (!m) return ink;
  const n = parseInt(m[1], 16);
  const L = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return L > 0.6 ? ink : '#ffffff';
}

const INK = '#0f172a';
const DEFAULT_FONT = 'Nunito';
const SECTION_CONTENT = 'Content';
const SECTION_LAYOUT = 'Layout';
const SECTION_COLORS = 'Colors';
const SECTION_FONTS = 'Fonts';

const MAX_LINES = 5;

// ---------------------------------------------------------------------------
// Smart-fill seam — term/definition from the first concept, examples from the
// example/non-example lists.
// ---------------------------------------------------------------------------

const contentSource: ContentSourceSpec = {
  kind: 'standard',
  wants: ['concepts', 'examples', 'nonExamples'],
  applyContent(payload: StandardContentPayload, current: Record<string, unknown>) {
    const next = { ...current };
    const concept = Array.isArray(payload?.concepts) ? payload.concepts[0] : undefined;
    if (concept) {
      // concept.name is a full standard restatement — too long for the center
      // oval (owner live-test: a 111-char term overflowed ~11 lines). Cap it.
      if (concept.name) {
        next.term = concept.name.length > 42 ? `${concept.name.slice(0, 42).trim()}…` : concept.name;
      }
      if (concept.definition) next.definition = concept.definition;
    }
    // examples / nonExamples: on a fill, ALWAYS reflect the payload — set to
    // its content, or CLEAR to blank when the standard gives none. Never retain
    // the sample defaults (owner 2026-07-24: a filled Frayer must not print the
    // polygon sample examples on a vowel card).
    const ex = Array.isArray(payload?.examples) ? payload.examples : [];
    next.examples = ex.slice(0, MAX_LINES).map((e) => e?.text ?? '').filter(Boolean).join('\n');
    const nex = Array.isArray(payload?.nonExamples) ? payload.nonExamples : [];
    next.nonExamples = nex.slice(0, MAX_LINES).map((e) => e?.text ?? '').filter(Boolean).join('\n');
    // Characteristics has NO graph source — clear it on a fill so a filled
    // Frayer never carries the off-topic default (polygon traits on a vowel
    // card). An honest blank quadrant is the teacher's/students' to complete.
    next.characteristics = '';
    if (payload?.standard?.code) next.sourceStandardCode = payload.standard.code;
    if (payload?.standard?.id) next.sourceStandardId = payload.standard.id;
    return next;
  },
};

const frayerModel: ToolRecipe = {
  id: 'frayer-model',
  domain: 'ela',
  category: 'practice',
  tags: ['vocabulary', 'frayer', 'graphic-organizer', 'concept', 'definition', 'standards'],
  featured: true,
  name: 'Frayer Model',
  version: 1,
  description:
    'The four-quadrant vocabulary organizer — a term in the center ringed by its definition, characteristics, examples and non-examples. Fill it in, or print it blank for students.',
  defaults: {
    term: 'Polygon',
    definition: 'A closed, flat shape made of straight line segments that meet at corners.',
    characteristics: 'Made of straight sides\nClosed shape (no gaps)\nSides meet at vertices\nNamed by its number of sides',
    examples: 'Triangle\nSquare\nPentagon\nHexagon',
    nonExamples: 'Circle\nCrescent moon\nAn open zig-zag\nA shape with a curved side',
    leaveBlanks: false,
    definitionFill: '#eef2ff',
    characteristicsFill: '#ecfeff',
    examplesFill: '#f0fdf4',
    nonExamplesFill: '#fef2f2',
    centerFill: '#fde68a',
    textColor: INK,
    textFont: DEFAULT_FONT,
    termFont: '',
    bodyFont: '',
    sourceStandardCode: '',
    sourceStandardId: '',
  },
  schema: [
    { key: 'term', label: 'Term', type: 'text', section: SECTION_CONTENT, help: 'The word or concept in the center oval.' },
    { key: 'definition', label: 'Definition', type: 'multiline', section: SECTION_CONTENT },
    { key: 'characteristics', label: 'Characteristics', type: 'multiline', section: SECTION_CONTENT, help: 'One per line — shown as bullets.' },
    { key: 'examples', label: 'Examples', type: 'multiline', section: SECTION_CONTENT, help: 'One per line.' },
    { key: 'nonExamples', label: 'Non-examples', type: 'multiline', section: SECTION_CONTENT, help: 'One per line.' },
    {
      key: 'leaveBlanks',
      label: 'Blank worksheet',
      type: 'boolean',
      section: SECTION_LAYOUT,
      help: 'Print ruled quadrants for students to fill in (the term still shows).',
    },
    { key: 'definitionFill', label: 'Definition fill', type: 'fill', section: SECTION_COLORS, help: 'Top-left quadrant.' },
    { key: 'characteristicsFill', label: 'Characteristics fill', type: 'fill', section: SECTION_COLORS, help: 'Top-right quadrant.' },
    { key: 'examplesFill', label: 'Examples fill', type: 'fill', section: SECTION_COLORS, help: 'Bottom-left quadrant.' },
    { key: 'nonExamplesFill', label: 'Non-examples fill', type: 'fill', section: SECTION_COLORS, help: 'Bottom-right quadrant.' },
    { key: 'centerFill', label: 'Center oval fill', type: 'fill', section: SECTION_COLORS },
    { key: 'textColor', label: 'Text color', type: 'color', section: SECTION_COLORS },
    { key: 'textFont', label: 'Text font (default for all parts)', type: 'font', section: SECTION_FONTS },
    { key: 'termFont', label: 'Term font', type: 'font', section: SECTION_FONTS },
    { key: 'bodyFont', label: 'Body font', type: 'font', section: SECTION_FONTS },
    { key: 'sourceStandardCode', label: 'Source standard code', type: 'text', section: SECTION_CONTENT, advanced: true, help: 'Set automatically when filled from a standard.' },
    { key: 'sourceStandardId', label: 'Source standard id', type: 'text', section: SECTION_CONTENT, advanced: true, help: 'Set automatically when filled from a standard.' },
  ],
  modal: {
    hero: ['term', 'definition'],
    columns: 2,
    sectionOrder: [SECTION_CONTENT, SECTION_LAYOUT, SECTION_COLORS, SECTION_FONTS],
  },
  gradeBands: {
    K2: { textFont: 'Fredoka' },
    G35: { textFont: 'Nunito' },
    G68: { textFont: 'Lexend' },
    G912: { textFont: 'Arial' },
  },
  contentSource,
  async generate(params) {
    const W = 760;
    const H = 760;
    const cx = W / 2;
    const cy = H / 2;
    const pad = 26;
    const rx = 104;
    const ry = 66;
    const cellW = cx;

    const leaveBlanks = bool(params.leaveBlanks, false);
    const textColor = str(params.textColor, INK);
    const textFont = str(params.textFont, DEFAULT_FONT);
    const partFont = (key: string): string => {
      const v = params[key];
      return typeof v === 'string' && v.trim() !== '' ? v : textFont;
    };
    const termFont = partFont('termFont');
    const bodyFont = partFont('bodyFont');

    const objects: FabricObject[] = [];
    const fillJobs: { obj: FabricObject; fill: FillValue; fallback: string }[] = [];

    // Four quadrant backgrounds tile the square exactly; dividers + frame draw
    // on top, then the center oval crowns the intersection.
    const quads: { fill: FillValue; fb: string; left: number; top: number }[] = [
      { fill: (params.definitionFill ?? '#eef2ff') as FillValue, fb: '#eef2ff', left: 0, top: 0 },
      { fill: (params.characteristicsFill ?? '#ecfeff') as FillValue, fb: '#ecfeff', left: cx, top: 0 },
      { fill: (params.examplesFill ?? '#f0fdf4') as FillValue, fb: '#f0fdf4', left: 0, top: cy },
      { fill: (params.nonExamplesFill ?? '#fef2f2') as FillValue, fb: '#fef2f2', left: cx, top: cy },
    ];
    for (const q of quads) {
      const r = new Rect({ left: q.left, top: q.top, width: cellW, height: cy });
      fillJobs.push({ obj: r, fill: q.fill, fallback: q.fb });
      objects.push(r);
    }

    // Divider cross + outer frame (share the ink so the teacher tunes one color).
    objects.push(new Line([cx, 0, cx, H], { stroke: textColor, strokeWidth: 3 }));
    objects.push(new Line([0, cy, W, cy], { stroke: textColor, strokeWidth: 3 }));

    // Quadrant headings, each pinned to its OUTER corner so the oval never
    // collides with them.
    const heading = (label: string, x: number, y: number, align: 'left' | 'right') =>
      new FabricText(label, {
        left: x,
        top: y,
        originX: align,
        originY: 'top',
        fontFamily: bodyFont,
        fontSize: 24,
        fontWeight: 'bold',
        fill: textColor,
      });
    objects.push(heading('Definition', pad, pad, 'left'));
    objects.push(heading('Characteristics', W - pad, pad, 'right'));
    objects.push(heading('Examples', pad, cy + 16, 'left'));
    objects.push(heading('Non-Examples', W - pad, cy + 16, 'right'));

    // Content — a wrapped block per quadrant, OR ruled writing lines when blank.
    const contentW = cellW - pad * 2;
    const topContent = pad + 40;
    const botContent = cy + ry + 22;
    const bullets = (v: unknown) => splitLines(v).slice(0, MAX_LINES).map((l) => `•  ${l}`).join('\n');

    const blocks: { text: string; left: number; top: number }[] = [
      { text: rawStr(params.definition, '').trim(), left: pad, top: topContent },
      { text: bullets(params.characteristics), left: cx + pad, top: topContent },
      { text: bullets(params.examples), left: pad, top: botContent },
      { text: bullets(params.nonExamples), left: cx + pad, top: botContent },
    ];

    if (leaveBlanks) {
      // Four ruled writing zones (kept clear of the oval — top rules stop above
      // it, bottom rules start below it).
      const ruleXs: { x: number; top: number }[] = [
        { x: pad, top: topContent + 8 },
        { x: cx + pad, top: topContent + 8 },
        { x: pad, top: botContent },
        { x: cx + pad, top: botContent },
      ];
      for (const z of ruleXs) {
        for (let i = 0; i < 4; i++) {
          const ly = z.top + 6 + i * 34;
          objects.push(
            new Line([z.x, ly, z.x + contentW, ly], { stroke: textColor, strokeWidth: 1, opacity: 0.3 }),
          );
        }
      }
    } else {
      for (const b of blocks) {
        if (!b.text) continue;
        objects.push(
          new Textbox(b.text, {
            left: b.left,
            top: b.top,
            width: contentW,
            fontSize: 20,
            fontFamily: bodyFont,
            fill: textColor,
            textAlign: 'left',
            lineHeight: 1.2,
            originX: 'left',
            originY: 'top',
          }),
        );
      }
    }

    // Outer frame on top of the fills (unfilled, non-evented so it never eats
    // hits meant for the content beneath).
    objects.push(
      new Rect({ left: 0, top: 0, width: W, height: H, fill: '', stroke: textColor, strokeWidth: 4, evented: false }),
    );

    // Center oval + term (crowns everything).
    const centerFill = (params.centerFill ?? '#fde68a') as FillValue;
    const oval = new Ellipse({ left: cx, top: cy, rx, ry, originX: 'center', originY: 'center', stroke: textColor, strokeWidth: 3 });
    fillJobs.push({ obj: oval, fill: centerFill, fallback: '#fde68a' });
    objects.push(oval);
    objects.push(
      new Textbox(str(params.term, 'Term'), {
        left: cx,
        top: cy,
        width: rx * 2 - 20,
        fontSize: 34,
        fontFamily: termFont,
        fontWeight: 'bold',
        fill: readableInkOn(solidFromFill(centerFill, '#fde68a'), textColor),
        textAlign: 'center',
        originX: 'center',
        originY: 'center',
      }),
    );

    await Promise.all(fillJobs.map((j) => applyFill(j.obj, j.fill, j.fallback)));

    const group = new Group(objects);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, frayerModel, params);
  },
};

registerRecipe(frayerModel);
